import { Canceler } from 'axios';
import { action, computed, makeObservable, observable, toJS } from 'mobx';
import { ICustomersModalItem } from '../types';
import { defaultPagination, PER_PAGE } from 'src/constants/main';
import { modalStore } from 'src/mobx/modalStore';
import client from 'src/services/client';
import { IPagination, MODALS_CRUMB } from 'src/types/main';
import { ICustomer } from 'src/types/user';
import { getCancelToken, getTranslate } from 'src/utils/helpers';
import { findSubstring } from 'src/utils/parser';
import logger from '~/services/logger';

class CustomerModalStore {
  constructor() {
    makeObservable(this);
  }

  cancel: Canceler | null = null;

  @observable loading: boolean = false;

  /**
   * If nothing is selected, then all are selected
   *
   * @type {boolean}
   */
  @observable isEmptyThisAll: boolean = false;
  @observable activeCrumb: MODALS_CRUMB = MODALS_CRUMB.ALL;
  @observable searchValue: string = '';

  @observable selectedCustomers: ICustomersModalItem[] = [];

  @observable pagination: IPagination<ICustomersModalItem> = defaultPagination;

  @computed get isMulti(): boolean {
    return Boolean(modalStore.customersModal?.isMulti);
  }

  @computed get isSelectedCrumb(): boolean {
    return this.activeCrumb === MODALS_CRUMB.SELECTED;
  }

  @computed get alreadySelectedUnavailableCustomers(): ICustomersModalItem[] {
    return modalStore.customersModal?.selectedCustomers?.filter((customer) => customer.is_delete) || [];
  }

  @computed get selectedAvailableCustomers(): ICustomersModalItem[] {
    return this.selectedCustomers.reduce((sum: ICustomersModalItem[], current) => {
      if (this.alreadySelectedUnavailableCustomers.some((customer) => customer.id === current.id)) {
        return sum;
      }

      return sum.concat(current);
    }, []);
  }

  public initStore = () => {
    this.loading = false;
    this.isEmptyThisAll = !modalStore.customersModal?.selectedCustomers?.length && this.isMulti;
    this.activeCrumb = MODALS_CRUMB.ALL;
    this.searchValue = '';
    this.selectedCustomers = toJS(modalStore.customersModal?.selectedCustomers) || [];
    this.pagination = defaultPagination;

    this.fetchCustomers(1);
  };

  @action fetchCustomers = async (page: number = this.pagination.current_page + 1) => {
    try {
      if (this.cancel) this.cancel('Cancelled');
      if (this.isSelectedCrumb && !this.selectedCustomers.length && !this.isEmptyThisAll) return;
      this.loading = true;

      const pagination: IPagination<ICustomer> = await client.get(`/v3/users`, {
        params: {
          page,
          search: this.searchValue,
          ids: this.fetchIdsParams,
          is_delete: 0,
          status: 1,
        },
        cancelToken: getCancelToken((c) => (this.cancel = c)),
      });

      this.pagination = { ...pagination, data: pagination.data.map((customer) => this.transformCustomer(customer)) };
      this.loading = false;
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.loading = false;
      logger.error({ error, place: 'CustomerModalStore fetchCustomers' });
    }
  };
  private get fetchIdsParams(): number[] | undefined {
    return this.isSelectedCrumb && !this.isEmptyThisAll ? this.selectedCustomers.map((customer) => customer.id) : undefined;
  }
  private transformCustomer = (customer: ICustomer): ICustomersModalItem => {
    return {
      id: customer.id,
      name: customer.name,
      mobile: customer.mobile,
      is_delete: customer.is_delete,
      status: customer.status,
    };
  };

  @action changeCrumb = (crumb: MODALS_CRUMB) => {
    this.activeCrumb = crumb;
    this.fetchCustomers(1);
  };

  @action onSearch = (search: string) => {
    this.searchValue = search;
    this.fetchCustomers(1);
  };

  @action selectCustomer = (customer: ICustomersModalItem) => {
    const alreadySelected: boolean = this.selectedCustomers.some((_customer) => customer.id === _customer.id);

    if (alreadySelected) {
      this.selectedCustomers = this.selectedCustomers.filter((_customer) => customer.id !== _customer.id);
      return;
    }

    if (this.isMulti) {
      this.selectedCustomers = this.selectedCustomers.concat(customer);
      this.isEmptyThisAll = false;
      if (this.isSelectedCrumb) this.fetchCustomers(1);

      return;
    }

    this.selectedCustomers = [customer];
  };

  @action deselect = () => {
    this.selectedCustomers = [];
    this.isEmptyThisAll = false;
  };

  @action selectAll = () => {
    if (this.searchValue) {
      this.fetchAllCustomers();
      return;
    }

    this.selectedCustomers = this.alreadySelectedUnavailableCustomers;
    this.isEmptyThisAll = true;
    if (this.isSelectedCrumb) this.fetchCustomers(1);
  };

  @action deselectAll = () => {
    if (this.searchValue) {
      this.fetchAllCustomers();
      return;
    }

    this.selectedCustomers = [];
    this.isEmptyThisAll = false;
    if (this.isSelectedCrumb) this.fetchCustomers(1);
  };

  private fetchAllCustomers = async () => {
    try {
      if (this.cancel) this.cancel('Cancelled');
      this.loading = true;

      const pagination: IPagination<ICustomer> = await client.get(`/v3/users`, {
        params: {
          page: 1,
          per_page: PER_PAGE.MAXIMUM,
          search: this.searchValue,
          ids: this.fetchIdsParams,
          is_delete: 0,
          status: 1,
        },
        cancelToken: getCancelToken((c) => (this.cancel = c)),
      });

      this.selectedCustomers = pagination.data.map((customer) => this.transformCustomer(customer));
      this.loading = false;
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.loading = false;
      logger.error({ error, place: 'CustomerModalStore fetchAllCustomers' });
    }
  };

  public checkIncludeInFilter = (customer: ICustomersModalItem): boolean => {
    return findSubstring(getTranslate(customer.name), this.searchValue) || findSubstring(getTranslate(customer.mobile), this.searchValue);
  };

  @action clickSave = () => {
    modalStore.customersModal?.onSave(this.selectedCustomers);
    this.closeModal();
  };

  @action closeModal = () => {
    modalStore.closeCustomersModal();
    this.clearStore();
  };
  private clearStore = () => {
    if (this.cancel) this.cancel('Cancelled');
  };
}

export const customerModalStore = new CustomerModalStore();
